import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createConnectAccount,
    createAccountLinkForCurrentUser,
    getPayoutAccount,
    requestPayout,
    fetchPayoutHistory,
    cancelPayout,
    getPayoutStats,
    getEventPayoutSummary,
    fetchOrganizerTransactions,
    refreshPayoutAccount
} from '../apiEndpoints';
import { Button, Input, Chip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/react';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import { addToast } from '@heroui/toast';
import { useAuth } from '../contexts/auth-context';
import { currencyCode, resolveCurrency, type EventCurrency } from '../utils/currency';
import { btnAccent, btnGhost, btnPrimary, card, darkPanel, sectionTitle, bodyMuted, surface, label as labelCls, Segmented } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText };
const labeledInputCls = { inputWrapper: `min-h-14 ${fieldWrap}`, input: fieldText, label: 'text-grey40 dark:text-nightMuted' };
const selectCls = { trigger: `min-h-14 ${fieldWrap}`, value: fieldText, label: 'text-grey40 dark:text-nightMuted' };
const modalCls = {
    base: 'rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-nightSurface dark:ring-white/10',
    header: 'px-6 pt-6 text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white',
    body: 'px-6',
    footer: 'gap-2 px-6 pb-6',
    closeButton: 'right-4 top-4 rounded-full hover:bg-grey99 dark:hover:bg-white/10',
    backdrop: 'bg-black/50',
};
const statTile = `${surface} p-6`;
const statLabel = 'text-xs font-bold uppercase tracking-[0.16em] text-grey40 dark:text-nightMuted';
const statValue = 'mt-3 text-[clamp(30px,3.6vw,40px)] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const statNote = 'mt-3 text-sm text-grey40 dark:text-nightMuted';
const statFee = 'mt-1 text-xs text-grey50 dark:text-nightMuted';
const skel = 'rounded-full bg-grey95 dark:bg-white/10';
const statusChipCls = { base: 'h-7 rounded-full px-1', content: 'text-xs font-semibold capitalize' };
const cellLabel = 'text-xs font-semibold uppercase tracking-[0.12em] text-grey50 dark:text-nightMuted';
const cellValue = 'mt-1 text-sm font-semibold text-grey10 dark:text-white';
const smallPill = 'inline-flex h-9 min-w-0 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors';
const panelTitle = 'text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white';

interface PayoutRequest {
    amount: number;
    eventId?: string;
    description?: string;
    relatedOrders?: string[];
}

// interface PayoutAccount {
//     stripeAccountId: string;
//     status: string;
//     businessType?: string;
//     companyName?: string;
//     email?: string;
//     country?: string;
//     currency?: string;
//     payoutsEnabled?: boolean;
//     chargesEnabled?: boolean;
//     requirements?: any;
// }

interface Payout {
    payoutId: string;
    eventId: any;
    amount: number;
    fee: number;
    netAmount: number;
    stripeFee?: number;
    currency?: string;
    status: string;
    description?: string;
    requestedAt?: string;
    approvedAt?: string;
    processedAt?: string;
    rejectionReason?: string;
    failureReason?: string;
}

export default function PayoutManagement() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('request');
    const [currency, setCurrency] = useState<EventCurrency>('usd');
    const [payoutRequest, setPayoutRequest] = useState<PayoutRequest>({
        amount: 0,
        description: ''
    });

    // Refresh account modal state
    const { isOpen: isRefreshModalOpen, onOpen: onRefreshModalOpen, onClose: onRefreshModalClose } = useDisclosure();
    const [refreshEmail, setRefreshEmail] = useState(user?.email || '');

    // Fetch payout account
    const { data: payoutAccount, isFetching: accountLoading } = useQuery({
        queryKey: ['payoutAccount'],
        queryFn: getPayoutAccount,
        retry: false,
        enabled: !!user
    });

    // Fetch payout history
    const { data: payoutHistory, isFetching: historyLoading } = useQuery({
        queryKey: ['payoutHistory', currency],
        queryFn: () => fetchPayoutHistory({ page: 1, limit: 20, currency }),
        enabled: !!user
    });

    // Fetch payout stats
    const { data: payoutStats, isFetching: statsLoading } = useQuery({
        queryKey: ['payoutStats', currency],
        queryFn: () => getPayoutStats(currency),
        enabled: !!user
    });

    const { data: eventSummary, isFetching: eventSummaryLoading } = useQuery({
        queryKey: ['eventPayoutSummary'],
        queryFn: getEventPayoutSummary,
        enabled: !!user
    });

    // Fetch organizer transactions
    const { data: transactionsData, isFetching: transactionsLoading } = useQuery({
        queryKey: ['organizerTransactions'],
        queryFn: () => fetchOrganizerTransactions({ page: 1, limit: 100 }),
        enabled: !!user
    });

    // Mutations
    const connectAccountMutation = useMutation({
        mutationFn: createConnectAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payoutAccount'] });
        }
    });

    const requestPayoutMutation = useMutation({
        mutationFn: requestPayout,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payoutHistory'] });
            queryClient.invalidateQueries({ queryKey: ['payoutStats'] });
            queryClient.invalidateQueries({ queryKey: ['eventPayoutSummary'] });
            setPayoutRequest({
                amount: 0,
                description: ''
            });
        },
        onError: (error: any) => {
            console.error('Error requesting payout:', error);
            let errorMessage = 'Failed to request payout. Please try again.';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message?.includes('Insufficient platform balance')) {
                errorMessage = 'Platform has insufficient funds. Please try again later.';
            } else if (error.message?.includes('exceeds your available payout amount')) {
                errorMessage = 'Requested amount exceeds your available balance.';
            } else if (error.message?.includes('Connected account not found')) {
                errorMessage = 'Your payout account is not properly set up. Please contact support.';
            } else if (error.message?.includes('not properly configured for payouts')) {
                errorMessage = 'Your payout account needs additional verification. Please complete the setup process.';
            } else if (error.message?.includes('Stripe processing fee')) {
                errorMessage = 'Payout amount must be sufficient to cover processing fees (2.9% + $0.30).';
            }

            addToast({
                title: 'Error',
                description: errorMessage,
                color: 'danger'
            });
        }
    });

    const cancelPayoutMutation = useMutation({
        mutationFn: cancelPayout,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payoutHistory'] });
            queryClient.invalidateQueries({ queryKey: ['payoutStats'] });
        },
        onError: (error: any) => {
            console.error('Error canceling payout:', error);
            let errorMessage = 'Failed to cancel payout. Please try again.';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message?.includes('not in requested status')) {
                errorMessage = 'This payout cannot be cancelled. Only requested payouts can be cancelled.';
            } else if (error.message?.includes('not found')) {
                errorMessage = 'Payout not found. It may have already been processed.';
            }

            addToast({
                title: 'Error',
                description: errorMessage,
                color: 'danger'
            });
        }
    });


    const createAccountLinkMutation = useMutation({
        mutationFn: createAccountLinkForCurrentUser,
        onSuccess: (accountLink) => {
            if (accountLink) {
                window.open(accountLink, '_blank');
                // Refresh account data after opening the link
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ['payoutAccount'] });
                }, 2000);
            }
        },
        onError: (error: any) => {
            console.error('Error creating account link:', error);
            addToast({
                title: 'Error',
                description: `Failed to create account link: ${error.message || 'Unknown error'}`,
                color: 'danger'
            });
        }
    });

    const handleConnectAccount = async () => {
        try {
            const result = await connectAccountMutation.mutateAsync({
                type: 'express',
                country: 'US',
                email: user?.email || '',
                businessType: 'individual'
            });

            // If account link is provided, redirect to Stripe onboarding
            if (result.accountLink) {
                window.open(result.accountLink, '_blank');
            } else {
                // If no account link but account was created, try to create one
                try {
                    const accountLinkResult = await createAccountLinkForCurrentUser({});
                    if (accountLinkResult) {
                        window.open(accountLinkResult, '_blank');
                    } else {
                        addToast({
                            title: 'Success!',
                            description: 'Payout account created successfully! You can now request payouts.',
                            color: 'success'
                        });
                    }
                } catch (linkError) {
                    console.error('Error creating account link:', linkError);
                    addToast({
                        title: 'Success!',
                        description: 'Payout account created successfully! You can now request payouts.',
                        color: 'success'
                    });
                }
            }
        } catch (error: any) {
            console.error('Error creating connect account:', error);

            // Provide more specific error messages based on the error type
            let errorMessage = 'Failed to create payout account. Please try again.';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message?.includes('Invalid account data')) {
                errorMessage = 'Please check your account information and try again.';
            } else if (error.message?.includes('Stripe API error')) {
                errorMessage = 'Payment service is temporarily unavailable. Please try again later.';
            } else if (error.message?.includes('Authentication error')) {
                errorMessage = 'There was an authentication issue. Please contact support.';
            }

            addToast({
                title: 'Error',
                description: errorMessage,
                color: 'danger'
            });
        }
    };

    const handleCompleteSetup = () => {
        createAccountLinkMutation.mutate({});
    };

    const refreshAccountMutation = useMutation({
        mutationFn: refreshPayoutAccount,
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['payoutAccount'] });
            onRefreshModalClose();
            addToast({
                title: 'Success',
                description: 'Payout account refreshed successfully. Please complete the setup process.',
                color: 'success'
            });
            // If account link is provided, redirect to Stripe onboarding
            if (result.accountLink) {
                window.open(result.accountLink, '_blank');
            }
        },
        onError: (error: any) => {
            console.error('Error refreshing payout account:', error);
            let errorMessage = 'Failed to refresh payout account. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            addToast({
                title: 'Error',
                description: errorMessage,
                color: 'danger'
            });
        }
    });

    const handleRefreshAccount = () => {
        // Pre-fill with current user email
        setRefreshEmail(user?.email || '');
        onRefreshModalOpen();
    };

    const handleSubmitRefreshAccount = async () => {
        if (!refreshEmail) {
            addToast({
                title: 'Validation Error',
                description: 'Email is required',
                color: 'warning'
            });
            return;
        }

        // Use existing account data for other fields, or defaults
        const accountData = {
            email: refreshEmail,
            type: 'express' as 'express' | 'standard' | 'custom', // Default to express
            country: payoutAccount?.country || 'US',
            businessType: payoutAccount?.businessType || 'individual',
            companyName: payoutAccount?.companyName || '',
            phone: payoutAccount?.phone || ''
        };

        await refreshAccountMutation.mutateAsync(accountData);
    };

    const handleRequestPayout = async () => {
        if (!selectedPayoutEvent) {
            addToast({
                title: 'Validation Error',
                description: 'Select the event you want to request a payout for',
                color: 'warning'
            });
            return;
        }
        if (payoutRequest.amount <= 0) {
            addToast({
                title: 'Validation Error',
                description: 'Please enter a valid payout amount',
                color: 'warning'
            });
            return;
        }

        // Calculate minimum amount: amount - fee > 0, so amount > fee
        // fee = amount * 0.03 + 0.3, so amount > amount * 0.03 + 0.3
        // amount * 0.97 > 0.3, so amount > 0.3 / 0.97 ≈ 0.31
        const minAmount = 1.0;
        if (payoutRequest.amount < minAmount) {
            const fee = calculateStripePayoutFee(payoutRequest.amount);
            addToast({
                title: 'Validation Error',
                description: `Minimum payout amount is ${formatCurrency(minAmount, requestCurrency)} (to cover Stripe processing fee of ${formatCurrency(fee, requestCurrency)})`,
                color: 'warning'
            });
            return;
        }

        if (payoutRequest.amount > maxAmount) {
            addToast({
                title: 'Validation Error',
                description: `Payout amount cannot exceed the available revenue of ${formatCurrency(maxAmount, requestCurrency)} for this event`,
                color: 'warning'
            });
            return;
        }

        try {
            await requestPayoutMutation.mutateAsync(payoutRequest);
            addToast({
                title: 'Success!',
                description: 'Payout request submitted successfully!',
                color: 'success'
            });
        } catch (error: any) {
            // Error handling is now done in the mutation's onError callback
            console.error('Error requesting payout:', error);
        }
    };

    const handleCancelPayout = async (payoutId: string) => {
        if (confirm('Are you sure you want to cancel this payout request?')) {
            try {
                await cancelPayoutMutation.mutateAsync(payoutId);
            } catch (error) {
                // Error handling is now done in the mutation's onError callback
                console.error('Error canceling payout:', error);
            }
        }
    };


    const getStatusColor = (status: string) => {
        switch (status) {
            case 'requested': return 'warning';
            case 'approved': return 'primary';
            case 'processing': return 'secondary';
            case 'completed': return 'success';
            case 'failed': return 'danger';
            case 'cancelled': return 'default';
            case 'rejected': return 'danger';
            default: return 'default';
        }
    };

    const formatCurrency = (amount: number, code: string = currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode(code)
        }).format(amount);
    };

    const payoutEvents: any[] = (eventSummary?.events || [])
        .filter((event: any) => (event.revenue?.totalRevenue || 0) > 0)
        .sort((a: any, b: any) => new Date(b.eventDate || 0).getTime() - new Date(a.eventDate || 0).getTime());
    const selectedPayoutEvent = payoutEvents.find((event: any) => String(event.eventId) === payoutRequest.eventId);
    const requestCurrency = selectedPayoutEvent?.currency || currency;
    const maxAmount: number = selectedPayoutEvent?.payouts?.availableToPayout || 0;
    const limitedByBalance: boolean = !!selectedPayoutEvent && (selectedPayoutEvent.payouts?.eventAvailable ?? 0) > maxAmount;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calculate Stripe payout processing fee
    const calculateStripePayoutFee = (amount: number): number => {
        if (amount <= 0) {
            return 0;
        }
        // Stripe charges 3% of the amount plus $0.30
        return amount * 0.03 + 0.3;
    };

    if (!user) {
        return (
            <div className="flex justify-center items-center h-64">
                <p className={bodyMuted}>Please log in to access payout management</p>
            </div>
        );
    }



    return (
        <div className="mx-auto w-full pb-8">
            <div className="mb-8">
                <h1 className={sectionTitle}>Payout Management</h1>
                <p className={`mt-3 max-w-[560px] ${bodyMuted}`}>Manage your payout account and request payouts for your events</p>
            </div>

            {accountLoading ? (
                <div className={`mb-6 ${surface} p-6`}>
                    <div className={`h-6 w-48 animate-pulse ${skel}`}></div>
                    <div className="mt-4 space-y-3">
                        <div className={`h-4 w-full animate-pulse ${skel}`}></div>
                        <div className={`h-4 w-3/4 animate-pulse ${skel}`}></div>
                        <div className={`mt-4 h-12 w-40 animate-pulse ${skel}`}></div>
                    </div>
                </div>
            ) : !payoutAccount && (
                <div className={`mb-6 ${surface} p-6 sm:p-8`}>
                    <h2 className={panelTitle}>Set Up Payout Account</h2>
                    <p className={`mt-3 mb-6 max-w-[640px] ${bodyMuted}`}>
                        To receive payouts, you need to set up a Stripe Connect account. This allows us to transfer funds directly to your bank account.
                    </p>
                    <Button
                        color="primary"
                        className={`${btnAccent} w-full sm:w-auto`}
                        onClick={handleConnectAccount}
                        isLoading={connectAccountMutation.isPending}
                    >
                        Set Up Payout Account
                    </Button>
                </div>
            )}

            {accountLoading ? (
                <div className={`mb-6 ${surface} p-5 sm:p-6`}>
                    <div className="animate-pulse space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className={`h-7 w-16 ${skel}`}></div>
                                <div className={`h-4 w-32 ${skel}`}></div>
                            </div>
                            <div className="flex gap-2">
                                <div className={`h-9 w-20 ${skel}`}></div>
                                <div className={`h-9 w-24 ${skel}`}></div>
                            </div>
                        </div>
                        <div className={`h-3 w-48 ${skel}`}></div>
                    </div>
                </div>
            ) : payoutAccount && (
                <div className={`mb-6 ${surface} p-5 sm:p-6`}>
                    <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                            <Chip
                                color={payoutAccount.status === 'active' ? 'success' : 'warning'}
                                variant="flat"
                                size="sm"
                                classNames={statusChipCls}
                            >
                                {payoutAccount.status}
                            </Chip>
                            <span className="truncate text-base font-semibold text-grey10 dark:text-white">
                                {payoutAccount.companyName || 'Payout Account'}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                size="sm"
                                variant="light"
                                className={`${smallPill} bg-transparent text-grey10 ring-1 ring-grey90 data-[hover=true]:bg-grey99 dark:text-white dark:ring-white/15 dark:data-[hover=true]:bg-white/[0.06]`}
                                onClick={handleRefreshAccount}
                                isLoading={accountLoading}
                                isDisabled={accountLoading}
                            >
                                Refresh
                            </Button>
                            {payoutAccount.status === 'active' && (
                                <Button
                                    size="sm"
                                    color="primary"
                                    variant="light"
                                    className={`${smallPill} no-global-button-color bg-grey10 text-white data-[hover=true]:bg-black dark:bg-white dark:text-nightBg dark:data-[hover=true]:bg-grey90`}
                                    onClick={handleCompleteSetup}
                                    isLoading={createAccountLinkMutation.isPending}
                                >
                                    Edit Account
                                </Button>
                            )}
                            {payoutAccount.status === 'pending' && (
                                <Button
                                    size="sm"
                                    color="warning"
                                    className={`${smallPill} bg-primary text-white data-[hover=true]:bg-primary30 dark:bg-nightAccent dark:text-nightBg`}
                                    onClick={handleCompleteSetup}
                                    isLoading={createAccountLinkMutation.isPending}
                                >
                                    Complete Setup
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="break-all text-xs text-grey40 dark:text-nightMuted">
                        <span className="font-mono">{payoutAccount.stripeAccountId}</span>
                        {payoutAccount.currency && (
                            <span className="ml-2">• {payoutAccount.currency.toUpperCase()}</span>
                        )}
                    </div>
                    <div className="mt-2 break-all text-xs text-grey40 dark:text-nightMuted">
                        <span className="font-mono">{payoutAccount.email}</span>
                    </div>
                </div>
            )}

            {(payoutStats?.currencies?.length || 0) > 1 && (
                <div className="mb-6 flex flex-wrap items-center gap-3">
                    <span className="text-sm font-semibold text-grey40 dark:text-nightMuted">Currency</span>
                    <div className="inline-flex rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]" role="group" aria-label="Payout currency">
                        {(payoutStats.currencies as string[]).map(code => {
                            const value = resolveCurrency(code);
                            const active = value === currency;
                            return (
                                <button
                                    key={value}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => setCurrency(value)}
                                    className={`h-9 rounded-full px-4 text-sm font-semibold transition-all ${active ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg' : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'}`}
                                >
                                    {currencyCode(value)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mb-8">
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className={`${darkPanel} rounded-[28px] p-6`}>
                            <div className="pointer-events-none absolute -right-16 -top-16 -z-10 h-48 w-48 rounded-full bg-nightAccent/25 blur-[60px] max-md:hidden" />
                            {statsLoading ? (
                                <div className="animate-pulse">
                                    <div className="mb-3 h-4 w-24 rounded-full bg-white/10"></div>
                                    <div className="mb-3 h-10 w-32 rounded-full bg-white/10"></div>
                                    <div className="h-3 w-28 rounded-full bg-white/10"></div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">Balance</p>
                                        <p className="mt-3 text-[clamp(30px,3.6vw,40px)] font-bold leading-none tracking-[-0.04em] text-white">
                                            {formatCurrency(payoutStats?.balance || 0)}
                                        </p>
                                        <p className="mt-3 text-sm text-white/60">Settled (after 3 days)</p>
                                        {payoutStats?.settledFeeAmount > 0 && (
                                            <p className="mt-1 text-xs text-white/50">
                                                Order Fees: {formatCurrency(payoutStats.settledFeeAmount)}
                                            </p>
                                        )}
                                        {payoutStats?.settledStripeFees > 0 && (
                                            <p className="mt-1 text-xs text-white/50">
                                                Stripe Fees: {formatCurrency(payoutStats.settledStripeFees)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={statTile}>
                            {statsLoading ? (
                                <div className="animate-pulse">
                                    <div className={`mb-3 h-4 w-32 ${skel}`}></div>
                                    <div className={`mb-3 h-10 w-32 ${skel}`}></div>
                                    <div className={`h-3 w-28 ${skel}`}></div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        <p className={statLabel}>Pending Balance</p>
                                        <p className={statValue}>
                                            {formatCurrency(payoutStats?.pendingBalance || 0)}
                                        </p>
                                        <p className={statNote}>Last 3 days (settling)</p>
                                        {payoutStats?.pendingFeeAmount > 0 && (
                                            <p className={statFee}>
                                                Order Fees: {formatCurrency(payoutStats.pendingFeeAmount)}
                                            </p>
                                        )}
                                        {payoutStats?.pendingStripeFees > 0 && (
                                            <p className={statFee}>
                                                Stripe Fees: {formatCurrency(payoutStats.pendingStripeFees)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={statTile}>
                            {statsLoading ? (
                                <div className="animate-pulse">
                                    <div className={`mb-3 h-4 w-32 ${skel}`}></div>
                                    <div className={`mb-3 h-10 w-32 ${skel}`}></div>
                                    <div className={`h-3 w-28 ${skel}`}></div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        <p className={statLabel}>Total Balance</p>
                                        <p className={statValue}>
                                            {formatCurrency(payoutStats?.totalBalance || 0)}
                                        </p>
                                        <p className={statNote}>Balance + Pending</p>
                                        {payoutStats?.totalBalanceFeeAmount > 0 && (
                                            <p className={statFee}>
                                                Order Fees: {formatCurrency(payoutStats.totalBalanceFeeAmount)}
                                            </p>
                                        )}
                                        {payoutStats?.totalBalanceStripeFees > 0 && (
                                            <p className={statFee}>
                                                Stripe Fees: {formatCurrency(payoutStats.totalBalanceStripeFees)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className={statTile}>
                            {eventSummaryLoading || statsLoading ? (
                                <div className="animate-pulse">
                                    <div className={`mb-3 h-4 w-24 ${skel}`}></div>
                                    <div className={`mb-3 h-10 w-32 ${skel}`}></div>
                                    <div className={`h-3 w-28 ${skel}`}></div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        <p className={statLabel}>Total Revenue</p>
                                        <p className={statValue}>
                                            {formatCurrency(payoutStats?.totalRevenue || 0)}
                                        </p>
                                        <p className={statNote}>From all events</p>
                                        {payoutStats?.totalRevenueFeeAmount > 0 && (
                                            <p className={statFee}>
                                                Order Fees: {formatCurrency(payoutStats.totalRevenueFeeAmount)}
                                            </p>
                                        )}
                                        {payoutStats?.totalRevenueStripeFees > 0 && (
                                            <p className={statFee}>
                                                Stripe Fees: {formatCurrency(payoutStats.totalRevenueStripeFees)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={statTile}>
                            {statsLoading ? (
                                <div className="animate-pulse">
                                    <div className={`mb-3 h-4 w-24 ${skel}`}></div>
                                    <div className={`mb-3 h-10 w-32 ${skel}`}></div>
                                    <div className={`h-3 w-28 ${skel}`}></div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        <p className={statLabel}>Total Payouts</p>
                                        <p className={statValue}>
                                            {formatCurrency(payoutStats?.totals?.totalAmount || 0)}
                                        </p>
                                        <p className={statNote}>Amount paid out</p>
                                        {/* {payoutStats?.totals?.totalStripeFees !== undefined && (
                                            <p className="text-teal-200 text-xs mt-0.5">
                                                Stripe Fees: {formatCurrency(payoutStats.totals.totalStripeFees)}
                                            </p>
                                        )} */}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-6 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <Segmented
                    label="Payout sections"
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[
                        { key: 'request', label: 'Request Payout' },
                        { key: 'history', label: 'Payout History' },
                        { key: 'transactions', label: 'Transactions' },
                    ]}
                />
            </div>


            {activeTab === 'request' && (
                <div className={`${card} p-5 sm:p-8`}>
                    <h2 className={`${panelTitle} mb-6`}>Request Payout</h2>
                    <div>
                        {eventSummaryLoading && !eventSummary ? (
                            <div className="space-y-5 animate-pulse">
                                <div>
                                    <div className={`mb-2 h-4 w-24 ${skel}`}></div>
                                    <div className="h-14 w-full rounded-2xl bg-grey95 dark:bg-white/10"></div>
                                </div>
                                <div>
                                    <div className={`mb-2 h-4 w-28 ${skel}`}></div>
                                    <div className="h-24 w-full rounded-[20px] bg-grey95 dark:bg-white/10"></div>
                                </div>
                                <div className="pt-4">
                                    <div className={`h-12 w-36 ${skel}`}></div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-5">
                                <div>
                                    <Select
                                        label="Event"
                                        placeholder={eventSummaryLoading ? 'Loading events...' : payoutEvents.length ? 'Select the event to pay out' : 'No events with revenue yet'}
                                        selectedKeys={payoutRequest.eventId ? [payoutRequest.eventId] : []}
                                        isDisabled={payoutEvents.length === 0}
                                        onSelectionChange={keys => {
                                            const value = Array.from(keys)[0];
                                            setPayoutRequest(prev => ({ ...prev, eventId: value ? String(value) : undefined, amount: 0 }));
                                        }}
                                        classNames={selectCls}
                                    >
                                        {payoutEvents.map((event: any) => (
                                            <SelectItem
                                                key={String(event.eventId)}
                                                textValue={event.eventTitle}
                                                description={`${formatCurrency(event.payouts?.availableToPayout || 0, event.currency)} available`}
                                            >
                                                {event.eventTitle}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                    <p className="mt-2 text-xs text-grey40 dark:text-nightMuted">Payouts are paid from one event's revenue at a time, after its sales have settled (3 days).</p>
                                </div>
                                {selectedPayoutEvent && (
                                    <div className={`${surface} p-5`}>
                                        <h4 className="mb-4 text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">{selectedPayoutEvent.eventTitle}</h4>
                                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                                            <div>
                                                <span className={cellLabel}>Event Revenue:</span>
                                                <p className={cellValue}>{formatCurrency(selectedPayoutEvent.revenue?.totalRevenue || 0, requestCurrency)}</p>
                                            </div>
                                            <div>
                                                <span className={cellLabel}>Available to Pay Out:</span>
                                                <p className="mt-1 text-2xl font-bold tracking-[-0.03em] text-primary dark:text-nightAccent">{formatCurrency(maxAmount, requestCurrency)}</p>
                                            </div>
                                            <div>
                                                <span className={cellLabel}>Settling (last 3 days):</span>
                                                <p className={cellValue}>{formatCurrency(selectedPayoutEvent.payouts?.pendingRevenue || 0, requestCurrency)}</p>
                                            </div>
                                            <div>
                                                <span className={cellLabel}>Already Paid Out or Requested:</span>
                                                <p className={cellValue}>{formatCurrency((selectedPayoutEvent.payouts?.completedAmount || 0) + (selectedPayoutEvent.payouts?.pendingAmount || 0), requestCurrency)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className={labelCls}>
                                        Amount ({currencyCode(requestCurrency)})
                                    </label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            value={payoutRequest.amount.toString()}
                                            onChange={(e) => {
                                                const inputValue = parseFloat(e.target.value) || 0;
                                                setPayoutRequest(prev => ({ ...prev, amount: Math.min(inputValue, maxAmount) }));
                                            }}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="1.00"
                                            max={maxAmount}
                                            isDisabled={!selectedPayoutEvent}
                                            className="flex-1"
                                            classNames={inputCls}
                                        />
                                        <Button
                                            size="sm"
                                            variant="bordered"
                                            className={`${btnGhost} h-12 min-w-0 border-0 bg-transparent px-5`}
                                            onClick={() => setPayoutRequest(prev => ({ ...prev, amount: maxAmount }))}
                                            isDisabled={maxAmount <= 0}
                                        >
                                            Max
                                        </Button>
                                    </div>
                                    <div className="mt-2">
                                        <p className="text-xs text-grey40 dark:text-nightMuted">
                                            {selectedPayoutEvent ? `Maximum available for this event: ${formatCurrency(maxAmount, requestCurrency)}${limitedByBalance ? '. Limited by your overall balance after earlier payouts.' : ''}` : 'Select an event to see how much you can pay out.'}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>
                                        Description
                                    </label>
                                    <Input
                                        value={payoutRequest.description}
                                        onChange={(e) => setPayoutRequest(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Payout description (optional)"
                                        classNames={inputCls}
                                    />
                                </div>
                                <div className="pt-2">
                                    <Button
                                        color="primary"
                                        className={`${btnAccent} w-full sm:w-auto`}
                                        onClick={handleRequestPayout}
                                        isLoading={requestPayoutMutation.isPending}
                                        disabled={!selectedPayoutEvent || payoutRequest.amount <= 0 || maxAmount <= 0}
                                    >
                                        Request Payout
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'history' && (
                <div className={`${card} overflow-hidden`}>
                    <div className="px-5 pt-6 pb-4 sm:px-8">
                        <h2 className={panelTitle}>Payout History</h2>
                    </div>
                    <div>
                        {historyLoading ? (
                            <div className="animate-pulse divide-y divide-black/[0.06] dark:divide-white/10">
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="px-5 py-5 sm:px-8">
                                        <div className="mb-2 flex items-start justify-between">
                                            <div className="space-y-2">
                                                <div className={`h-5 w-32 ${skel}`}></div>
                                                <div className={`h-4 w-24 ${skel}`}></div>
                                            </div>
                                            <div className="space-y-2 text-right">
                                                <div className={`ml-auto h-5 w-20 ${skel}`}></div>
                                                <div className={`ml-auto h-7 w-16 ${skel}`}></div>
                                            </div>
                                        </div>
                                        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                                            <div className="space-y-1">
                                                <div className={`h-3 w-16 ${skel}`}></div>
                                                <div className={`h-4 w-20 ${skel}`}></div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className={`h-3 w-16 ${skel}`}></div>
                                                <div className={`h-4 w-20 ${skel}`}></div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className={`h-3 w-20 ${skel}`}></div>
                                                <div className={`h-4 w-24 ${skel}`}></div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className={`h-3 w-20 ${skel}`}></div>
                                                <div className={`h-4 w-24 ${skel}`}></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : payoutHistory?.payouts?.length > 0 ? (
                            <div className="divide-y divide-black/[0.06] border-t border-black/[0.06] dark:divide-white/10 dark:border-white/10">
                                {payoutHistory.payouts.map((payout: Payout) => (
                                    <div key={payout.payoutId} className="px-5 py-5 transition-colors hover:bg-grey99/60 sm:px-8 dark:hover:bg-white/[0.02]">
                                        <div className="mb-4 flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <p className="break-all font-bold text-grey10 dark:text-white">Payout #{payout.payoutId}</p>
                                                <p className="mt-0.5 text-sm text-grey40 dark:text-nightMuted">
                                                    {payout.eventId?.title || 'Event'}
                                                </p>
                                            </div>
                                            <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
                                                <p className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{formatCurrency(payout.netAmount, payout.currency)}</p>
                                                <Chip
                                                    color={getStatusColor(payout.status)}
                                                    variant="flat"
                                                    size="sm"
                                                    classNames={statusChipCls}
                                                >
                                                    {payout.status}
                                                </Chip>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                                            <div>
                                                <p className={cellLabel}>Amount</p>
                                                <p className={cellValue}>{formatCurrency(payout.netAmount, payout.currency)}</p>
                                            </div>
                                            {/* <div>
                                                <p className="text-gray-500">Stripe Fee</p>
                                                <p>{formatCurrency(payout.stripeFee || calculateStripePayoutFee(payout.amount), payout.currency)}</p>
                                            </div> */}
                                            <div>
                                                <p className={cellLabel}>Requested</p>
                                                <p className={cellValue}>{payout.requestedAt ? formatDate(payout.requestedAt) : '-'}</p>
                                            </div>
                                            <div>
                                                <p className={cellLabel}>Processed</p>
                                                <p className={cellValue}>{payout.processedAt ? formatDate(payout.processedAt) : '-'}</p>
                                            </div>
                                        </div>
                                        {payout.description && (
                                            <p className="mt-3 text-sm text-grey40 dark:text-nightMuted">{payout.description}</p>
                                        )}
                                        {payout.rejectionReason && (
                                            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                                                Rejection reason: {payout.rejectionReason}
                                            </p>
                                        )}
                                        {payout.failureReason && (
                                            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                                                Failure reason: {payout.failureReason}
                                            </p>
                                        )}
                                        {payout.status === 'requested' && (
                                            <div className="mt-4">
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    variant="light"
                                                    className={`${smallPill} bg-transparent text-red-600 ring-1 ring-red-500/30 data-[hover=true]:bg-red-500/10 dark:text-red-400`}
                                                    onClick={() => handleCancelPayout(payout.payoutId)}
                                                    isLoading={cancelPayoutMutation.isPending}
                                                >
                                                    Cancel Request
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="px-5 pb-8 sm:px-8">
                                <div className="rounded-[20px] bg-grey99 px-6 py-12 text-center dark:bg-white/[0.04]">
                                    <p className={bodyMuted}>No payout history found</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'transactions' && (
                <div className={`${card} overflow-hidden`}>
                    <div className="px-5 pt-6 pb-4 sm:px-8">
                        <h2 className={panelTitle}>Transactions</h2>
                    </div>
                    <div>
                        {!transactionsLoading && transactionsData?.data?.totalSum !== undefined && (
                            <div className="px-5 pb-5 sm:px-8">
                                <div className={`${surface} p-5 sm:p-6`}>
                                    <div className="flex items-end justify-between gap-4">
                                        <div className="min-w-0">
                                            <p className={statLabel}>Total Transactions</p>
                                            <p className="mt-2 break-words text-[clamp(26px,3vw,36px)] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white">
                                                {formatCurrency(transactionsData.data.totalSumByCurrency?.[currency] ?? (currency === 'usd' ? transactionsData.data.totalSum || 0 : 0))}
                                            </p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <p className={statLabel}>Total Count</p>
                                            <p className="mt-2 text-2xl font-bold leading-none tracking-[-0.03em] text-grey10 dark:text-white">
                                                {transactionsData.data.total || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {transactionsLoading ? (
                            <div className="animate-pulse divide-y divide-black/[0.06] dark:divide-white/10">
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="px-5 py-5 sm:px-8">
                                        <div className="mb-2 flex items-start justify-between">
                                            <div className="space-y-2">
                                                <div className={`h-5 w-32 ${skel}`}></div>
                                                <div className={`h-4 w-24 ${skel}`}></div>
                                            </div>
                                            <div className="space-y-2 text-right">
                                                <div className={`ml-auto h-5 w-20 ${skel}`}></div>
                                                <div className={`ml-auto h-7 w-16 ${skel}`}></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : transactionsData?.data?.data?.length > 0 ? (
                            <div className="divide-y divide-black/[0.06] border-t border-black/[0.06] dark:divide-white/10 dark:border-white/10">
                                {transactionsData.data.data.map((transaction: any) => (
                                    <div key={transaction.paymentId || transaction._id} className="px-5 py-5 transition-colors hover:bg-grey99/60 sm:px-8 dark:hover:bg-white/[0.02]">
                                        <div className="mb-2 flex items-start justify-between">
                                            <div className="min-w-0 flex-1">
                                                <p className="mb-1 text-sm font-semibold text-grey40 dark:text-nightMuted">
                                                    {transaction.date || transaction.createdAt ? formatDate(transaction.date || transaction.createdAt) : '-'}
                                                </p>
                                                {!transaction.isGuest && (transaction.customerName || transaction.customerEmail) && (
                                                    <div className="mt-2 min-w-0">
                                                        <p className="text-base font-bold text-grey10 dark:text-white">
                                                            {transaction.customerName || 'Customer'}
                                                        </p>
                                                        {transaction.customerEmail && (
                                                            <p className="break-all text-xs text-grey40 dark:text-nightMuted">{transaction.customerEmail}</p>
                                                        )}
                                                        {transaction.customerPhone && (
                                                            <p className="text-xs text-grey40 dark:text-nightMuted">{transaction.customerPhone}</p>
                                                        )}
                                                    </div>
                                                )}
                                                {transaction.isGuest && (transaction.guestName || transaction.guestEmail) && (
                                                    <div className="mt-2 min-w-0">
                                                        <p className="text-base font-bold text-grey10 dark:text-white">
                                                            {transaction.guestName || 'Guest'}
                                                        </p>
                                                        {transaction.guestEmail && (
                                                            <p className="break-all text-xs text-grey40 dark:text-nightMuted">{transaction.guestEmail}</p>
                                                        )}
                                                        {transaction.guestPhone && (
                                                            <p className="text-xs text-grey40 dark:text-nightMuted">{transaction.guestPhone}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ml-4 shrink-0 text-right">
                                                <p className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{formatCurrency(transaction.amount || 0, transaction.currency)}</p>
                                                <p className="mt-1 inline-flex rounded-full bg-grey99 px-2.5 py-0.5 text-xs font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Net Amount</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 rounded-[20px] bg-grey99 p-4 dark:bg-white/[0.04]">
                                            <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                                                <div>
                                                    <p className={cellLabel}>Total Amount</p>
                                                    <p className={cellValue}>{formatCurrency(transaction.totalAmount || 0, transaction.currency)}</p>
                                                </div>
                                                <div>
                                                    <p className={cellLabel}>Order Fee</p>
                                                    <p className={cellValue}>{formatCurrency(transaction.feeAmount || 0, transaction.currency)}</p>
                                                </div>
                                                <div>
                                                    <p className={cellLabel}>Stripe Fee</p>
                                                    <p className={cellValue}>{formatCurrency(transaction.stripeFee || 0, transaction.currency)}</p>
                                                </div>
                                                <div>
                                                    <p className={cellLabel}>Net Amount</p>
                                                    <p className={cellValue}>{formatCurrency(transaction.amount || 0, transaction.currency)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="px-5 pb-8 sm:px-8">
                                <div className="rounded-[20px] bg-grey99 px-6 py-12 text-center dark:bg-white/[0.04]">
                                    <p className={bodyMuted}>No transactions found</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <Modal isOpen={isRefreshModalOpen} onClose={onRefreshModalClose} classNames={modalCls}>
                <ModalContent>
                    <ModalHeader>Refresh Payout Account</ModalHeader>
                    <ModalBody>
                        <div className="space-y-4">
                            <p className="text-sm text-grey40 dark:text-nightMuted">
                                This will delete your current payout account and create a new one with the email you specify.
                                You will need to complete the Stripe onboarding process again.
                            </p>
                            <Input
                                label="New Email"
                                type="email"
                                value={refreshEmail}
                                onChange={(e) => setRefreshEmail(e.target.value)}
                                placeholder="Enter new email address"
                                isRequired
                                classNames={labeledInputCls}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" className={`${btnGhost} bg-transparent`} onPress={onRefreshModalClose}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            className={`${btnPrimary} no-global-button-color`}
                            onPress={handleSubmitRefreshAccount}
                            isLoading={refreshAccountMutation.isPending}
                            isDisabled={!refreshEmail}
                        >
                            Refresh Account
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </div>
    );
}
